import { WEEKDAYS_SHORT, addDays, formatMonth, lastOfMonth, weekdayIndex } from '@krp/ui-kit'

import type { IsoDay } from './types'

/**
 * Месяц графика. Дни — строки `ГГГГ-ММ-ДД` по часам человека без часовых поясов и `toISOString`; неделя идёт с
 * понедельника. Месяц — строка `ГГГГ-ММ`. Всё здесь — чистые функции: «сегодня» приходит параметром, часы не читаются.
 */

/** Месяц `ГГГГ-ММ`. */
export type MonthValue = string

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/

export const isMonthValue = (value: string): boolean => MONTH.test(value)

/** Месяц, в котором день. */
export const monthOfDay = (day: IsoDay): MonthValue => day.slice(0, 7)

const firstOf = (month: MonthValue): IsoDay => `${month}-01`

/** Соседний месяц: вперёд или назад, через границу года тоже. */
export function shiftMonth(month: MonthValue, direction: -1 | 1): MonthValue {
  return direction > 0 ? monthOfDay(addDays(lastOfMonth(firstOf(month)), 1)) : monthOfDay(addDays(firstOf(month), -1))
}

export interface CalendarDay {
  day: IsoDay
  /** Номер дня недели с понедельника: 0 — Пн … 6 — Вс. */
  weekday: number
  weekdayShort: string
  dayNumber: number
  weekend: boolean
  today: boolean
}

export interface MonthCalendar {
  month: MonthValue
  /** «Октябрь 2026». */
  title: string
  /** Первое и последнее число месяца: ровно этот диапазон запрашивается у службы. */
  from: IsoDay
  to: IsoDay
  /** Все дни месяца по порядку: 28, 29, 30 или 31. */
  days: readonly CalendarDay[]
}

export function monthCalendar(month: MonthValue, today: IsoDay): MonthCalendar {
  const from = firstOf(month)
  const to = lastOfMonth(from)
  const count = Number(to.slice(8, 10))
  const days = Array.from({ length: count }, (_, index): CalendarDay => {
    const day = addDays(from, index)
    const weekday = weekdayIndex(day)
    return { day, weekday, weekdayShort: WEEKDAYS_SHORT[weekday] ?? '', dayNumber: index + 1, weekend: weekday >= 5, today: day === today }
  })
  return { month, title: formatMonth(from), from, to, days }
}
